import { useEffect, useMemo } from 'react';
import { getIsResourceAvailableToUse } from '@lp/features/RoutePlanner/utils';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { useClinicalSpecialistsForAssignment } from '@/queries/resource';
import { CandidateMatchType, Employee } from '@/api/apis/resources';
import { SuggestedEmployeeInfo } from './SuggestedEmployeeInfo';
import { useSuggestedRouteValidationContext } from './SuggestedRouteValidationContext';

type SuggestedResourceCSProps = {
  suggestedRouteId: string;
  resourceId: string;
  fallbackInfo: Pick<Employee, 'id' | 'firstName' | 'lastName' | 'profilePhoto'> | null;
  orTimeUTC: string;
};

export const SuggestedResourceCS = ({
  suggestedRouteId,
  resourceId,
  fallbackInfo,
  orTimeUTC,
}: SuggestedResourceCSProps) => {
  const { setCSResource } = useSuggestedRouteValidationContext();
  const { caseId, organType, donorType, caseName } = useBaseCaseDetails();
  const { data: csList, isLoading } = useClinicalSpecialistsForAssignment({
    caseId,
    donor: donorType,
    organ: organType,
    orTime: orTimeUTC,
  });

  const currentCS = useMemo(() => {
    if (!csList) return null;

    return csList.find((cs) => cs.employeeId === resourceId) ?? null;
  }, [resourceId, csList]);

  useEffect(() => {
    const isAvailable = getIsResourceAvailableToUse(currentCS);
    setCSResource(suggestedRouteId, isAvailable);
  }, [suggestedRouteId, currentCS, setCSResource]);

  const isWarningState = !currentCS || currentCS.match.matchType !== CandidateMatchType.Perfect;

  return (
    <SuggestedEmployeeInfo
      isLoading={isLoading}
      isWarningState={isWarningState}
      employee={currentCS}
      fallbackInfo={fallbackInfo}
      type="clinical-specialist"
      orTimeUTC={orTimeUTC}
      organType={organType}
      caseName={caseName}
    />
  );
};
